import { useEffect, useMemo } from 'react'
import { type UseFormReturn } from 'react-hook-form-7'
import { computeDerivedData } from './AccountForm.utils'
import { type FormData } from './AccountForm.types'

export const useAccountForm = ({
  register,
  unregister,
  setValue,
  watch,
  getValues,
}: UseFormReturn<FormData>) => {
  useEffect(() => {
    register('canonicalId')
    register('hasAllPartnersFullAccess')
    register('hasFullPartnerAccess')
    register('hasFullRetailerAccess')

    return () =>
      unregister([
        'canonicalId',
        'hasFullPartnerAccess',
        'hasFullRetailerAccess',
        'hasAllPartnersFullAccess',
      ])
  }, [register, unregister])

  watch([
    'partners',
    'retailers',
    'retailerLocations',
    'hasAllPartnersFullAccess',
    'hasFullRetailerAccess',
    'hasFullPartnerAccess',
  ])

  const {
    partners = [],
    retailers = [],
    hasAllPartnersFullAccess = false,
    hasFullRetailerAccess = false,
    hasFullPartnerAccess = false,
  } = getValues()

  const {
    hasFullPartnerAccess: computedHasFullPartnerAccess,
    hasFullRetailerAccess: computedHasFullRetailerAccess,
    canSelectPartners,
    canSelectRetailers,
    canSelectRetailerLocations,
    partnerIds,
    retailerIds,
  } = useMemo(
    () =>
      computeDerivedData({
        partners,
        retailers,
        hasAllPartnersFullAccess,
        hasFullRetailerAccess,
        hasFullPartnerAccess,
      }),
    [partners, retailers, hasAllPartnersFullAccess, hasFullRetailerAccess, hasFullPartnerAccess]
  )

  useEffect(() => {
    if (hasAllPartnersFullAccess) {
      //if the user has full access to all partners this implies full partner access
      setValue('hasFullPartnerAccess', true)
    }
  }, [hasAllPartnersFullAccess, setValue])

  useEffect(() => {
    setValue('hasFullPartnerAccess', computedHasFullPartnerAccess)
    if (computedHasFullPartnerAccess) {
      //if the user has full partner access this implies full retailer access
      setValue('hasFullRetailerAccess', true)
    }
  }, [computedHasFullPartnerAccess, setValue])

  useEffect(() => {
    setValue('hasFullRetailerAccess', computedHasFullRetailerAccess)
  }, [computedHasFullRetailerAccess, setValue])

  useEffect(() => {
    if (!canSelectRetailers) {
      setValue('retailers', [], { shouldValidate: true })
    }
  }, [setValue, canSelectRetailers])

  useEffect(() => {
    if (!canSelectRetailerLocations) {
      setValue('retailerLocations', [], { shouldValidate: true })
      setValue('retailerLocationsStoreGroups', [], { shouldValidate: true })
    }
  }, [setValue, canSelectRetailerLocations])

  useEffect(() => {
    if (!canSelectPartners) {
      setValue('partners', [], { shouldValidate: true })
    }
  }, [setValue, canSelectPartners])

  return {
    canSelectPartners,
    canSelectRetailers,
    canSelectRetailerLocations,
    hasFullPartnerAccess: computedHasFullPartnerAccess,
    hasFullRetailerAccess: computedHasFullRetailerAccess,
    hasAllPartnersFullAccess,
    partnerIds,
    retailerIds,
  }
}
